<script setup lang="ts">
/**
 * MY — Name Reservation (M32001) Preset Studio 패널.
 *
 * 이 프리셋만 **문서가 없다**(L_PRESET_TASK.DOC_CODES = NULL). SSM 이름 예약은
 * 생성할 서류 없이 곧바로 신고하는 절차라서다. 그래서 다른 Incorporation 패널처럼
 * 템플릿을 모아 생성하지 않고, task 만 만든 뒤 **이름 예약 e-Filing 화면으로 바로 넘긴다**.
 *
 *   Step1 제목 → Step2 T-Link 연결(또는 Skip) → Create Task → /tasks/efiling
 *
 * ※ PreSetIncorporationPrePanel 을 재사용하지 않은 이유: 그 패널은 연결된 템플릿이
 *   0건이면 "No templates linked to this preset — cannot generate documents" 로 막는다.
 */
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '../../../shared/preset/types/preset.types';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  editTaskId?: number | null;
  studio?: boolean;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'progress-change', pct: number): void;
}>();

const router = useRouter();
const creating = ref(false);
const progress = ref('');

/** 챗봇 Step2 에서 고른 T-Link 신청서(없으면 Skip 한 것). */
const linked = computed(() => {
  const f = props.chatFormData || {};
  const icpCd = String(f.icpCd || f.entityCd || '').trim();
  const name = String(f.entityName || f.company_name || '').trim();
  return icpCd ? { icpCd, name } : null;
});

const effectiveTitle = computed(() =>
  String(props.taskTitle || props.selection?.task?.TASK_NAME || 'Name Reservation').trim(),
);

/**
 * CRS 이름 예약은 **접미사를 뺀 이름**을 받는다(폼 안내: "Do not include SDN BHD / BERHAD suffix").
 * T-Link 는 이름과 접미사를 따로 들고 있으므로 그 값을 쓰는 게 정확하다 — 문자열을 자르는 건
 * 회사명 자체에 BERHAD 가 들어간 경우(예: YUNSEOK BERHAD) 잘못 잘릴 수 있어 마지막 수단이다.
 */
const linkedBareName = ref('');

/** 접미사 분리값을 못 구했을 때만 쓰는 폴백. 끝에 붙은 접미사 하나만 떼어 낸다. */
const SUFFIX_RE = /[\s,]+(SDN\.?\s*BHD\.?|BERHAD|BHD\.?|SENDIRIAN)\s*$/i;
function stripSuffix(name: string): string {
  return String(name || '').trim().replace(SUFFIX_RE, '').trim();
}

/**
 * T-Link 에서 그대로 옮길 수 있는 값들.
 *
 * DI 스냅샷(`INCORPORATION_DATA.directIncorporationFormData`)과 이름 예약 폼은
 * **같은 어휘**를 쓴다 — 실측으로 확인했다(2026-09-29):
 *   idType `PASSPORT` / compRegType `REGISTRATION_OF_COMPANIES` /
 *   isSsmRegistered `'true'` / memberCategory `FOREIGN_ENTITY_NT_REG_WITH_SSM` /
 *   businessCodes 는 양쪽 다 MSIC 코드 문자열(`'46510'`).
 * 그래서 코드 변환 없이 **이름만 바꿔** 옮긴다.
 *
 * 반대로 **카테고리·회사종류·접미사는 옮기지 않는다.** 저장된 스냅샷은 옛 형식의
 * 의미값(`LIMITED_BY_SHARES` / `SDN_BHD`)이고 `category`·`suffix` 는 아예 없는데,
 * 이름 예약 폼은 CRS 코드 id(`34125` / `34143`)를 쓴다. 억지로 옮기면 이름 조회가
 * 엉뚱한 조합으로 나간다 — 이 네 칸은 사용자가 고르게 둔다.
 */
const linkedPrefill = ref<Record<string, any>>({});

/** CRS 상한 — 프로모터 5명, 업종 3건. */
const MAX_PROMOTERS = 5;
const MAX_BUSINESS_CODES = 3;

const s = (v: any) => String(v ?? '').trim();

/**
 * T-Link 의 회사 구성(옛 형식) → CRS 코드.
 *
 * 저장된 `nameReservation` 은 설립 화면의 옛 어휘다. 실측(ICP 31건 전수)으로 확인한 형태는
 * `{category:null, companyType:'LIMITED_BY_SHARES', subType:'SDN_BHD', suffix:null,
 *   purpose:'NEW_INCORPORATION'}` 하나뿐이고, `category`·`suffix` 는 아예 없다.
 *
 * ★ 주의 — 설립의 `subType` 은 CRS 의 Sub Type(PRIVATE/PUBLIC)이 아니라 **접미사** 선택이다
 *   (company-subtypes.json: SDN. BHD. / BERHAD / SENDIRIAN). 그래서 CRS 의 Sub Type 과
 *   Suffix **두 칸**으로 갈라진다.
 * ★ Category 는 저장돼 있지 않다. 설립(Direct Incorporation)은 **국내 회사** 경로이므로
 *   LOCAL 로 둔다(외국회사 등록은 별도 신청이다).
 */
const CRS_CATEGORY_LOCAL = '34125';
const CRS_PURPOSE_LOCAL = '11071';
const CRS_TYPE_OF_APPLICATION = '34108'; // PROPOSED COMPANY NAME

const COMPANY_TYPE_TO_CRS: Record<string, string> = {
  LIMITED_BY_SHARES: '34101',
  LIMITED_BY_GUARANTEE: '34103',
  UNLIMITED: '34102',
};

/** 설립 subType(=접미사) → { CRS Sub Type, CRS Suffix }. */
const SUBTYPE_TO_CRS: Record<string, { subType: string; suffix: string }> = {
  SDN_BHD: { subType: '33939', suffix: '34143' },   // PRIVATE + SDN. BHD.
  BERHAD_1: { subType: '33940', suffix: '34148' },  // PUBLIC  + BERHAD
  SENDIRIAN: { subType: '33939', suffix: '34146' }, // (UNLIMITED) PRIVATE + SENDIRIAN
};

/** 옮길 수 있는 만큼만 옮긴다 — 못 맞추는 칸은 비워 두고 사용자가 고르게 한다. */
function nameReservationToCrs(nr: any): Record<string, string> {
  const out: Record<string, string> = {};
  if (!nr) return out;
  const ct = COMPANY_TYPE_TO_CRS[s(nr.companyType).toUpperCase()];
  const st = SUBTYPE_TO_CRS[s(nr.subType).toUpperCase()];
  // 회사종류를 못 맞추면 접미사만 넣어 봐야 캐스케이드가 어긋난다 — 통째로 포기한다.
  if (!ct) return out;
  out.category = CRS_CATEGORY_LOCAL;
  out.purpose = CRS_PURPOSE_LOCAL;
  out.companyType = ct;
  if (st) {
    // 보증유한회사는 CRS 에서 공개회사 고정이다.
    out.subType = ct === '34103' ? '33940' : st.subType;
    out.suffix = ct === '34103' ? '34148' : st.suffix;
  }
  out.typeOfApplication = CRS_TYPE_OF_APPLICATION;
  return out;
}

/** DI 주주 1건 → 이름 예약 프로모터 1건. */
function shareholderToPromoter(sh: any): Record<string, any> | null {
  if (!sh) return null;

  if (s(sh.type).toUpperCase() === 'BODYCORPORATE') {
    const name = s(sh.corporateName) || s(sh.entityName) || s(sh.name);
    if (!name) return null;
    const regNo = s(sh.compRegNo);
    return {
      promoterType: 'BODY_CORPORATE',
      promoterName: name,
      // DI 주주에는 **isSsmRegistered 칸이 없다**(compRegNo/compRegType 만 있다). 그대로 옮기면
      // 빈값이 되어 NR 폼이 "Registered with SSM is required" 로 제출을 막는다(2026-10-12 실측).
      // 없으면 등록번호 유무로 판정한다 — useCrsDraftPayload 의 법인 멤버 처리와 같은 규칙.
      // NR 폼 select 는 문자열 'true'/'false' 를 받는다(rn-form-config.json).
      isSsmRegistered: (() => {
        const raw = sh.isSsmRegistered;
        if (raw === undefined || raw === null || s(raw) === '') return regNo ? 'true' : 'false';
        return (raw === false || ['FALSE', 'NO', 'N', '0'].includes(s(raw).toUpperCase())) ? 'false' : 'true';
      })(),
      compRegType: s(sh.compRegType),
      // DI 에는 번호 **표기 형식**이 없다(칸 자체가 없다). 번호에서 도출한다 —
      // 신번호는 12자리 숫자, 그 밖은 구번호(`111111T`).
      compRegNoFormat: regNo ? (/^\d{12}$/.test(regNo) ? 'NEW' : 'OLD') : '',
      compRegNo: regNo,
      memberCategory: s(sh.memberCategory),
      memberSubCategory: s(sh.memberSubCategory),
      entityNo: s(sh.entityNo),
      email: s(sh.email),
    };
  }

  const name = s(sh.name);
  if (!name) return null;
  return {
    promoterType: 'INDIVIDUAL',
    promoterName: name,
    salutation: s(sh.salutation),
    identificationType: s(sh.idType),
    identificationNumber: s(sh.idNumber),
    idExpiryDate: s(sh.idExpiryDate),
    secondaryIdType: s(sh.secondaryIdType),
    secondaryIdNumber: s(sh.secondaryIdNumber),
    secondaryIdExpiryDate: s(sh.secondaryIdExpiryDate),
    dateOfBirth: s(sh.dob),
    email: s(sh.email),
  };
}

/** 연결한 T-Link 상세를 읽어 회사명과 프리필 값을 만든다. */
async function loadBareName(icpCd: string) {
  linkedBareName.value = '';
  linkedPrefill.value = {};
  if (!icpCd) return;
  try {
    const j = await fetch(`/api/setupEntity/incorporation/detail/${icpCd}`, {
      credentials: 'include',
    }).then((r) => r.json());
    const inc = (j?.data ?? j)?.incorporationData || {};
    const di = inc?.directIncorporationFormData || {};

    // DI 스냅샷의 nameReservation.entityName 이 이미 접미사를 뺀 값이다(같은 규칙을 공유한다).
    const fromDi = s(di?.nameReservation?.entityName);
    const fromRaw = s(inc?.companyName);
    linkedBareName.value = fromDi || fromRaw;

    const out: Record<string, any> = {};

    // 회사 구성(카테고리·회사종류·서브타입·접미사·신청종류).
    Object.assign(out, nameReservationToCrs(di?.nameReservation));

    // 업종(MSIC) — 양쪽 다 MSIC 코드 문자열이라 그대로 옮긴다.
    const codes = Array.isArray(di.businessCodes)
      ? di.businessCodes.map(s).filter(Boolean).slice(0, MAX_BUSINESS_CODES)
      : [];
    if (codes.length) out.businessCodes = codes;

    // Clarification — 1~5 는 번호가 같고, DI 의 **6 번이 이름 예약의 7 번**(Other Comments)이다.
    //   (이름 예약 6 번은 업종 선택표라 자유 입력이 아니다.)
    const c = di.clarifications || {};
    for (const n of ['01', '02', '03', '04', '05']) {
      const v = s(c[`clarification${n}`]);
      if (v) out[`clarification${n}`] = v;
    }
    const other = s(c.clarification06);
    if (other) out.clarification07 = other;

    // 프로모터 ← **이사만**.
    //   CRS 의 Promoter 는 Director·Shareholder 어느 쪽과도 자동 연결되지 않는 독립 입력이라
    //   무엇을 실을지는 우리가 정해야 한다. 처음엔 주주를 실었는데, 이사만 등록한 건이 CRS 에서
    //   프로모터 0건으로 나가는 민원이 있었고 **이사를 프로모터로 등록한다**는 고객 확인을 받아
    //   (2026-10-12) 이사 기준으로 바꿨다. 주주는 싣지 않는다 — 운영 결정.
    //
    //   ※ 참고(되돌릴 때를 위해): CRS·SSM 문서상으로는 프로모터가 이사보다 넓은 개념으로 보인다.
    //      SSM 설립 가이드라인은 director(s) **and** promoter(s) 를 나란히 요구하고,
    //      CRS Name Search 매뉴얼 p.23 은 "Either Individual or **Body Corporate**" 를 허용하며,
    //      법인 프로모터 칸은 "Category of **Member**", 최소 인원도 사원 법정 최소(비공개 1 /
    //      공개 2 / 보증유한 2)와 같다. 이사는 항상 자연인이라 이 설정에서는 법인 프로모터가
    //      프리필로 들어갈 일이 없다(필요하면 화면에서 직접 추가). 기준이 바뀌면
    //      아래 배열에 di.shareholders 를 더하기만 하면 된다 — 중복 제거는 이미 있다.
    //   이사 객체는 DI 스냅샷에서 주주와 같은 형태(name/idType/idNumber/dob/email…)라 변환기를 공유한다.
    //   같은 사람이 중복 등록된 경우를 대비해 신분증번호·법인등록번호로 중복을 지운다.
    const promoterKey = (p: Record<string, any>) => (
      p.promoterType === 'BODY_CORPORATE'
        ? `C:${s(p.compRegNo).toUpperCase() || s(p.promoterName).toUpperCase()}`
        : `I:${s(p.identificationNumber).replace(/[\s-]/g, '').toUpperCase() || s(p.promoterName).toUpperCase()}`
    );
    const seenPromoters = new Set<string>();
    const promoters: Record<string, any>[] = [];
    for (const src of [di.directors]) {
      for (const row of Array.isArray(src) ? src : []) {
        if (promoters.length >= MAX_PROMOTERS) break;
        const p = shareholderToPromoter(row);
        if (!p) continue;
        const key = promoterKey(p);
        if (seenPromoters.has(key)) continue;
        seenPromoters.add(key);
        promoters.push(p);
      }
    }
    if (promoters.length) out.promoters = promoters;

    linkedPrefill.value = out;
  } catch {
    /* 실패해도 폴백(stripSuffix)이 있으므로 조용히 넘어간다. */
  }
}

watch(
  () => linked.value?.icpCd || '',
  (cd) => { void loadBareName(cd); },
  { immediate: true },
);

/** 제안 회사명 — T-Link 를 연결했으면 미리 채워 두고, 아니면 e-Filing 에서 직접 입력한다. */
const proposedName = computed(() => linkedBareName.value || stripSuffix(linked.value?.name || ''));

/** 요약 카드에 보여 줄 "같이 넘어가는 것" 목록. */
const prefillSummary = computed(() => {
  const p = linkedPrefill.value || {};
  const parts: string[] = [];
  if (p.businessCodes?.length) parts.push(`${p.businessCodes.length} business code(s)`);
  if (p.promoters?.length) parts.push(`${p.promoters.length} promoter(s)`);
  const clar = ['clarification01', 'clarification02', 'clarification03',
    'clarification04', 'clarification05', 'clarification07'].filter((k) => p[k]).length;
  if (clar) parts.push(`${clar} clarification(s)`);
  if (p.companyType) parts.push('company details');
  return parts;
});

function gatherState() {
  return { icpCd: linked.value?.icpCd || '', entityName: proposedName.value };
}
function restoreState(_s: any) {
  /* 입력 칸이 없는 패널이라 복원할 로컬 상태가 없다. */
}

async function handleCreateTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) {
    progress.value = 'Please enter a Task Title.';
    return;
  }

  creating.value = true;
  progress.value = 'Creating task…';
  emit('progress-change', 30);

  try {
    // 문서를 만들지 않는 task — createDocument:false / proceedWithEFiling:true.
    //   task flow 를 이렇게 넘겨야 Task 상세가 문서 목록 대신 e-Filing 단계로 열린다.
    const taskFlow = { createDocument: false, requestTSign: false, proceedWithEFiling: true };

    const res = await fetch('/api/startTask/tasks', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        // 이름 예약은 아직 법인이 없다 — entityCd 는 비우고, 연결한 T-Link 는 icpCd 로 넘긴다.
        entityCd: null,
        icpCd: linked.value?.icpCd || null,
        taskTypeCd: props.selection?.task?.TASK_CODE || 'M32001',
        taskTypeName: props.selection?.task?.TASK_NAME || 'Name Reservation',
        taskTitle: effectiveTitle.value,
        taskFlow,
        // e-Filing 폼이 열릴 때 제안 이름을 미리 채우도록 실어 보낸다.
        //   ⚠️ 백엔드는 이 객체를 **그대로** `TASK_DATA.formData` 에 넣는다(tasks.js).
        //      그래서 여기서 `{ formData: … }` 로 한 번 더 감싸면 `formData.formData` 가 되어
        //      e-Filing 폼의 loadSavedData 가 영영 못 찾는다.
        //   ⚠️ 키는 폼이 읽는 이름과 같아야 한다 — 폼은 `entityName` 을 본다(`proposedName` 아님).
        officerData: {
          entityName: proposedName.value,
          icpCd: linked.value?.icpCd || null,
          // T-Link 에서 그대로 옮길 수 있는 값(업종·clarification·프로모터).
          //   폼의 loadSavedData 가 읽는 키와 같은 이름이라 별도 처리가 없다.
          ...linkedPrefill.value,
        },
      }),
    }).then((r) => r.json());

    const taskId = res?.data?.taskId || res?.taskId;
    if (!taskId) {
      progress.value = `Error creating task: ${res?.error?.message || res?.message || 'no taskId'}`;
      creating.value = false;
      return;
    }

    emit('progress-change', 100);
    emit('task-created', { taskId: Number(taskId), taskTitle: effectiveTitle.value });

    // 문서 단계가 없으므로 Task 상세를 거치지 않고 이름 예약 e-Filing 으로 바로 보낸다.
    router.push({ name: 'EFiling', query: { taskId: String(taskId) } });
  } catch (e: any) {
    progress.value = `Error: ${e?.message || e}`;
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask: handleCreateTask });
</script>

<template>
  <div class="nr-panel">
    <div class="nr-card">
      <h3 class="nr-title">Name Reservation</h3>
      <p class="nr-sub">
        Reserve a company name with SSM. No documents are generated for this preset — clicking
        <b>Create Task</b> opens the e-Filing form.
      </p>

      <dl class="nr-list">
        <dt>Task title</dt>
        <dd>{{ effectiveTitle || '—' }}</dd>

        <dt>Linked T-Link</dt>
        <dd v-if="linked">{{ linked.name || linked.icpCd }}</dd>
        <dd v-else class="muted">Not linked — the proposed name is entered in the e-Filing form.</dd>

        <dt>Proposed name</dt>
        <dd v-if="proposedName">{{ proposedName }}</dd>
        <dd v-else class="muted">To be entered</dd>

        <template v-if="linked">
          <dt>Carried over</dt>
          <dd v-if="prefillSummary.length">{{ prefillSummary.join(' · ') }}</dd>
          <dd v-else class="muted">Nothing else to carry over — enter the details in the e-Filing form.</dd>
        </template>
      </dl>

      <p v-if="progress" class="nr-progress" :class="{ err: /error/i.test(progress) }">
        {{ progress }}
      </p>
    </div>
  </div>
</template>

<style scoped>
.nr-panel {
  display: flex;
  justify-content: center;
  padding: 24px 16px;
  color: #111;
}
.nr-card {
  width: 100%;
  max-width: 640px;
  background: #fff;
  border: 1px solid #e6ebf3;
  border-radius: 14px;
  box-shadow: 0 6px 18px rgba(20, 30, 60, 0.06);
  padding: 22px 24px;
}
.nr-title {
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 800;
}
.nr-sub {
  margin: 0 0 18px;
  font-size: 13px;
  color: #5a6478;
  line-height: 1.6;
}
.nr-list {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 10px 14px;
  margin: 0;
  font-size: 14px;
}
.nr-list dt {
  color: #6b7688;
  font-weight: 600;
}
.nr-list dd {
  margin: 0;
  font-weight: 600;
}
.nr-list dd.muted {
  color: #93a0b4;
  font-weight: 400;
}
.nr-progress {
  margin: 18px 0 0;
  font-size: 13px;
  color: #1b64da;
}
.nr-progress.err {
  color: #d7263d;
}
</style>
